import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';

const services = [
  {
    title: 'Decor Management',
    image: '/decor_new.jpg',
  },
  {
    title: 'Vendor Management',
    image: '/vendor_new.jpg',
  },
  {
    title: 'Invitations & Gifting',
    image: 'https://source.unsplash.com/1200x1200/?wedding,invitation,gift',
  },
  {
    title: 'Entertainment',
    image: '/entertainment_new.jpg',
  },
];

export default function Gallery() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const totalSlides = Math.ceil(services.length / 2); // 2 items per slide on mobile

  useEffect(() => {
    const id = setInterval(() => {
      nextSlide();
    }, 3500);
    return () => clearInterval(id);
  }, [totalSlides]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  // Handle touch events for swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextSlide();
    }
    if (isRightSwipe) {
      prevSlide();
    }
  };
  return (
    <section className="py-24 bg-gradient-to-b from-[#395958] to-[#2a4342] text-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-14">
          <h3 className="text-3xl font-serif mb-4">Your Royal Affair, Perfected</h3>
          <p className="text-sm leading-relaxed max-w-3xl mx-auto" style={{ color: '#F1D291' }}>
            Palatial venues, show-stopping décor, world-class performances, and seamless execution, we turn your celebration into a masterpiece fit for royalty. From the first drumbeat to the last dance, every moment is choreographed to perfection.
          </p>
        </div>

        {/* Mobile carousel container */}
        <div className="block sm:hidden overflow-hidden" ref={containerRef}>
          <div
            className="flex transition-transform duration-500 ease-in-out"
            style={{
              transform: `translateX(-${currentSlide * 100}%)`,
              width: `${totalSlides * 100}%`
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {services.map((service, index) => (
              <motion.div
                key={`mobile-${service.title}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true, margin: '-100px' }}
                className="group flex-shrink-0 w-1/2 px-3"
              >
                {/* Image container - square */}
                <div className="relative overflow-hidden aspect-square bg-[#2a4342]">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      if (service.title === 'Invitations & Gifting') {
                        (e.currentTarget as HTMLImageElement).src = '/invite.png';
                      }
                    }}
                  />
                  <div className="absolute inset-0 bg-white/0 group-hover:bg-white/5 transition-colors" />
                </div>
                {/* Centered caption below image */}
                <p className="mt-4 text-lg font-serif text-center transition-all duration-300 group-hover:text-2xl">
                  {service.title}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Desktop grid - unchanged */}
        <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service) => (
            <motion.div
              key={`desktop-${service.title}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true, margin: '-100px' }}
              className="group"
            >
              {/* Image container - square */}
              <div className="relative overflow-hidden aspect-square bg-[#2a4342]">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    if (service.title === 'Invitations & Gifting') {
                      (e.currentTarget as HTMLImageElement).src = '/invite.png';
                    }
                  }}
                />
                <div className="absolute inset-0 bg-white/0 group-hover:bg-white/5 transition-colors" />
              </div>
              {/* Centered caption below image */}
              <p className="mt-4 text-lg font-serif text-center transition-all duration-300 group-hover:text-2xl">
                {service.title}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Mobile navigation */}
        <div className="flex items-center justify-center gap-6 mt-12 sm:hidden">
          <button
            onClick={prevSlide}
            className="w-9 h-9 rounded-full border border-white/30 flex items-center justify-center hover:bg-white/10 transition-colors"
          >
            ‹
          </button>
          <div className="flex items-center gap-2">
            {Array.from({ length: totalSlides }).map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`h-2 rounded-full transition-all ${index === currentSlide ? 'w-8 bg-white' : 'w-2 bg-white/30'
                  }`}
              />
            ))}
          </div>
          <button
            onClick={nextSlide}
            className="w-9 h-9 rounded-full border border-white/30 flex items-center justify-center hover:bg-white/10 transition-colors"
          >
            ›
          </button>
        </div>
      </div>
    </section>
  );
}
